import Axios from "axios";
import { alertController } from "@ionic/core";
import i18n from "../i18n";
import { APIBaseURL, LOGIN_URL } from "../utils/constant";
import { devLog } from "../utils/libs";
import { addRequestLog } from "./requestLog";

const axios = Axios.create({
  baseURL: APIBaseURL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Bootstrap default Authorization header from localStorage (in case interceptors
// haven't been attached yet on first render and early requests fire)
try {
  let raw = localStorage.getItem("pd-tokens");
  if (!raw) raw = localStorage.getItem("tokens"); // legacy fallback
  if (raw) {
    const parsed = JSON.parse(raw);
    const access = parsed?.access_token;
    if (access) {
      (axios.defaults.headers as any)["Authorization"] = `Bearer ${access}`;
    }
  }
} catch (_) {
  // ignore JSON/localStorage issues
}

// Create a function to attach interceptors that can use the auth context
export const setupInterceptors = (auth: AuthContextType) => {
  // Ensure idempotent setup by ejecting any existing interceptors first
  // We keep these IDs at module scope to avoid stacking duplicates
  if (typeof (window as any).__pdReqIx === "number") {
    axios.interceptors.request.eject((window as any).__pdReqIx);
    (window as any).__pdReqIx = undefined;
  }
  if (typeof (window as any).__pdResIx === "number") {
    axios.interceptors.response.eject((window as any).__pdResIx);
    (window as any).__pdResIx = undefined;
  }

  const reqId = axios.interceptors.request.use(
    (config) => {
      if (auth.tokens?.access_token) {
        config.headers["Authorization"] = `Bearer ${auth.tokens.access_token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
  (window as any).__pdReqIx = reqId;

  const resId = axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error?.response?.status;
      devLog("Axios error interceptor:", status || error?.message || error);

      const truncate = (val: unknown, limit = 500): string | undefined => {
        if (val === undefined || val === null) return undefined;
        const str = typeof val === "string" ? val : JSON.stringify(val);
        return str.length > limit ? str.slice(0, limit) + "…" : str;
      };

      const sanitizeHeaders = (headers: Record<string, string> | undefined): Record<string, string> | undefined => {
        if (!headers) return undefined;
        const out: Record<string, string> = {};
        for (const [k, v] of Object.entries(headers)) {
          out[k] = k.toLowerCase() === "authorization" ? "[redacted]" : String(v);
        }
        return out;
      };

      addRequestLog({
        method: (error.config?.method || "GET").toUpperCase(),
        url: error.config?.url || "unknown",
        status: status || null,
        statusText: error.response?.statusText,
        error: error.message || String(error),
        code: error.code,
        timestamp: Date.now(),
        requestData: truncate(error.config?.data),
        requestHeaders: sanitizeHeaders(error.config?.headers),
        responseData: truncate(error.response?.data),
        responseHeaders: sanitizeHeaders(error.response?.headers),
      });

      // Only act on HTTP responses; ignore pure network errors (no response)
      if (!error.response) {
        devLog("Network error (no response); skipping auth removal");
        return Promise.reject(error);
      }

      if (error.response.status === 401) {
        // If we already retried this request after a refresh, give up
        if ((error.config as any)?._retry) {
          devLog("401 error after retry - removing auth and redirecting to login");
          auth.removeAuth();
          window.location.href = LOGIN_URL;
          return Promise.reject(error);
        }

        // Attempt silent token refresh before logging out
        if (auth.tokens?.refresh_token) {
          try {
            const newTokens = await auth.refreshTokensSilently();
            if (newTokens) {
              // Retry the failed request with the new token
              const retryConfig = { ...error.config, _retry: true };
              retryConfig.headers["Authorization"] = `Bearer ${newTokens.access_token}`;
              return axios(retryConfig);
            }
          } catch {
            // Refresh failed — fall through to logout
          }
        }

        // Store the current route in session storage
        const currentRoute = window.location.pathname;
        sessionStorage.setItem("nextRoute", currentRoute);

        devLog("401 error - removing auth and redirecting to login");
        // Handle 401 unauthorized
        auth.removeAuth();
        window.location.href = LOGIN_URL; // Redirect to login if the response is 401 (Unauthorized)
      } else if (error.response.status === 403) {
        // Skip redirect for certain endpoints that should fail silently
        const url = error.config?.url || "";
        // Your Team at a glance 403s a manager with nobody in their line; the page explains it instead.
        const silentEndpoints = ["/chatbot-quota", "/translation-overrides", "/reports/team-glance"];
        const shouldSilentFail = silentEndpoints.some((endpoint) => url.includes(endpoint));

        if (!shouldSilentFail) {
          // Handle 403 Forbidden - but prevent infinite loops
          const alreadyRedirecting = sessionStorage.getItem("403-redirecting");
          if (!alreadyRedirecting) {
            sessionStorage.setItem("403-redirecting", "true");
            devLog("403 error - redirecting to dashboard");
            alertController.create({
              header: "Access Denied",
              message: "You are not authorized to perform this action.",
              buttons: ["OK"],
            }).then((alert) => alert.present());
            sessionStorage.removeItem("403-redirecting");
            window.location.href = "/app";
          }
        }
      } else {
        devLog("Other error:", error.message || error);
      }
      return Promise.reject(error);
    }
  );
  (window as any).__pdResIx = resId;
};

let tenantInterceptorId: number | null = null;

export const setupTenantInterceptor = (tenant: Tenant) => {
  devLog("[Axios] Setting up tenant interceptor for tenant:", tenant?.id);

  if (tenantInterceptorId !== null) {
    devLog("[Axios] Ejecting previous tenant interceptor:", tenantInterceptorId);
  axios.interceptors.request.eject(tenantInterceptorId);
  }

  tenantInterceptorId = axios.interceptors.request.use((config) => {
    if (tenant) {
      devLog("[Axios] Adding X-Tenant-ID header:", tenant.id, "to request:", config.url);
      config.headers["X-Tenant-ID"] = tenant.id;
    } else {
      devLog("[Axios] Tenant is null/undefined, not adding X-Tenant-ID header to:", config.url);
    }
    return config;
  });

  devLog("[Axios] Tenant interceptor ID:", tenantInterceptorId);
};

// Language interceptor - adds Accept-Language header based on current i18n language
// This is set up immediately and reads the current language on each request
let languageInterceptorId: number | null = null;

export const setupLanguageInterceptor = () => {
  if (languageInterceptorId !== null) {
    axios.interceptors.request.eject(languageInterceptorId);
  }

  languageInterceptorId = axios.interceptors.request.use((config) => {
    // Get current language from i18n - this will be updated when user changes language
    const currentLanguage = i18n.language || "en";
    devLog("[Axios] Accept-Language interceptor - i18n.language:", i18n.language, "-> sending:", currentLanguage);
    config.headers["Accept-Language"] = currentLanguage;
    return config;
  });
};

// Set up language interceptor immediately on module load
setupLanguageInterceptor();

// Also listen for language changes and log them
i18n.on("languageChanged", (lng) => {
  devLog("[Axios] i18n language changed to:", lng);
});

export default axios;